import React from 'react';
import { X, Copy } from 'lucide-react';
import { buildRenderTree } from '../utils/treeBuilder';

/**
 * 节点详情面板组件
 * 显示选中节点的 JSON 详情（带语法高亮）
 * 支持两种模式：完整JSON（node为null）和节点详情（node不为null）
 */
function NodeDetailPanel({ node, tree, onClose }) {
  // 确定显示的数据：如果没有选中节点，显示完整JSON
  const displayData = node ? node.value : (tree ? tree.value : null);
  const isFullJson = !node;

  if (!displayData) {
    return (
      <div className="flex items-center justify-center h-full bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500">
        <div className="text-center">
          <div className="text-4xl mb-2">📄</div>
          <div className="text-sm">暂无数据</div>
        </div>
      </div>
    );
  }

  // 格式化 JSON - 使用 buildRenderTree 重新构建以支持 JSON 字符串解析
  // 然后将 render tree 转换回原始对象进行格式化
  const getFormattedJson = () => {
    // 重新构建渲染树，这样可以触发 JSON 字符串解析
    const renderTree = buildRenderTree(displayData);
    // 将 render tree 转换回可以格式化的对象
    return JSON.stringify(convertRenderTreeToValue(renderTree), null, 2);
  };

  // 将 render tree 转换回原始值（包含解析后的 JSON）
  const convertRenderTreeToValue = (renderNode) => {
    if (!renderNode) return null;

    // 如果是从字符串解析的，使用解析后的值
    if (renderNode.isFromStringParse && renderNode.children) {
      return convertRenderTreeToValue({ ...renderNode, isFromStringParse: false });
    }

    // 如果是数组
    if (renderNode.type === 'array' && renderNode.children) {
      return renderNode.children.map(child => convertRenderTreeToValue(child));
    }

    // 如果是对象
    if (renderNode.type === 'object' && renderNode.children) {
      const obj = {};
      renderNode.children.forEach(child => {
        obj[child.key] = convertRenderTreeToValue(child);
      });
      return obj;
    }

    // 基本类型，直接返回值
    return renderNode.value;
  };

  const formattedJson = getFormattedJson();

  // 复制 JSON
  const handleCopy = () => {
    navigator.clipboard.writeText(formattedJson);
  };

  // 生成路径面包屑或标题
  const getTitle = () => {
    if (isFullJson) {
      return '完整 JSON';
    }
    const parts = node.id.split('.');
    return parts.join(' > ');
  };

  // 获取标题图标
  const getTitleIcon = () => {
    return isFullJson ? '📄' : '📍';
  };

  /**
   * 语法高亮渲染 JSON（使用 React 组件而不是 HTML 字符串）
   */
  const renderHighlightedJson = () => {
    const lines = formattedJson.split('\n');

    return lines.map((lineText, lineIndex) => {
      const tokens = parseJsonLine(lineText);

      return (
        <div key={lineIndex} className="flex hover:bg-gray-50 dark:hover:bg-gray-700/30">
          {/* 行号 */}
          <span className="inline-block w-12 text-right pr-4 text-gray-400 dark:text-gray-600 select-none flex-shrink-0">
            {lineIndex + 1}
          </span>
          {/* 代码内容 */}
          <span className="flex-1 whitespace-pre">
            {tokens.map((token, tokenIndex) => (
              <span key={tokenIndex} className={token.className}>
                {token.text}
              </span>
            ))}
          </span>
        </div>
      );
    });
  };

  /**
   * 解析 JSON 行为 tokens
   */
  const parseJsonLine = (line) => {
    const tokens = [];
    let i = 0;

    while (i < line.length) {
      // 跳过空格和缩进
      if (line[i] === ' ') {
        let spaces = '';
        while (i < line.length && line[i] === ' ') {
          spaces += line[i];
          i++;
        }
        tokens.push({ text: spaces, className: '' });
        continue;
      }

      // 字符串（键或值）
      if (line[i] === '"') {
        let str = '"';
        i++;
        while (i < line.length && line[i] !== '"') {
          if (line[i] === '\\') {
            str += line[i];
            i++;
            if (i < line.length) {
              str += line[i];
              i++;
            }
          } else {
            str += line[i];
            i++;
          }
        }
        if (i < line.length) {
          str += '"';
          i++;
        }

        // 判断是键还是值
        let j = i;
        while (j < line.length && line[j] === ' ') j++;
        const isKey = j < line.length && line[j] === ':';

        tokens.push({
          text: str,
          className: isKey
            ? 'text-fuchsia-600 dark:text-fuchsia-400'
            : 'text-emerald-600 dark:text-emerald-400'
        });
        continue;
      }

      // 数字
      if (/\d/.test(line[i]) || (line[i] === '-' && /\d/.test(line[i + 1]))) {
        let num = '';
        if (line[i] === '-') {
          num += line[i];
          i++;
        }
        while (i < line.length && /[\d.]/.test(line[i])) {
          num += line[i];
          i++;
        }
        tokens.push({ text: num, className: 'text-blue-500 dark:text-blue-400' });
        continue;
      }

      // 布尔值
      if (line.substring(i, i + 4) === 'true') {
        tokens.push({ text: 'true', className: 'text-orange-500 dark:text-orange-400' });
        i += 4;
        continue;
      }
      if (line.substring(i, i + 5) === 'false') {
        tokens.push({ text: 'false', className: 'text-orange-500 dark:text-orange-400' });
        i += 5;
        continue;
      }

      // null
      if (line.substring(i, i + 4) === 'null') {
        tokens.push({ text: 'null', className: 'text-gray-400 dark:text-gray-500 italic' });
        i += 4;
        continue;
      }

      // 符号
      if (/[{}[\]]/.test(line[i])) {
        tokens.push({ text: line[i], className: 'text-purple-600 dark:text-purple-400' });
        i++;
        continue;
      }

      // 冒号、逗号
      if (line[i] === ':' || line[i] === ',') {
        tokens.push({ text: line[i], className: 'text-gray-600 dark:text-gray-400' });
        i++;
        continue;
      }

      // 其他字符
      tokens.push({ text: line[i], className: '' });
      i++;
    }

    return tokens;
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-800">
      {/* 标题栏 */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 border-b border-blue-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-lg">{getTitleIcon()}</span>
          <span className="font-mono text-sm text-gray-700 dark:text-gray-200 truncate font-semibold" title={getTitle()}>
            {getTitle()}
          </span>
          {isFullJson && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
              👈 点击左侧节点查看详情
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 ml-4">
          {/* 复制按钮 */}
          <button
            onClick={handleCopy}
            className="px-3 py-1.5 text-sm font-medium bg-indigo-100 dark:bg-indigo-900 hover:bg-indigo-200 dark:hover:bg-indigo-800 text-indigo-700 dark:text-indigo-200 rounded-lg transition-all duration-200 flex items-center gap-1.5 border border-indigo-200 dark:border-indigo-700"
            title="复制 JSON"
          >
            <Copy size={14} />
            复制
          </button>
          {/* 关闭按钮（只在选中节点时显示） */}
          {!isFullJson && (
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              title="返回完整JSON"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {/* JSON 内容区 */}
      <div className="flex-1 overflow-auto bg-white dark:bg-gray-800">
        <div className="p-4 text-sm font-mono text-gray-800 dark:text-gray-200">
          {renderHighlightedJson()}
        </div>
      </div>
    </div>
  );
}

export default NodeDetailPanel;
